import React from "react";
import { CustomToastComponent } from "./Toast.styles";
import { ToastStyles } from "./Toast.variants";
import { useTheme } from "@mui/material/styles";
import Alert from "@mui/material/Alert";
import { SnackbarCloseReason, SnackbarProps } from "@mui/material/Snackbar";
import Slide, { SlideProps } from "@mui/material/Slide";
import { Icon, IconName } from "src/icons";

type ToastStylesReturnType = ReturnType<typeof ToastStyles>;
export type ToastVariants = keyof ToastStylesReturnType;

export interface CustomToastProps extends SnackbarProps {
    open: boolean;
    variant: ToastVariants;
    onClose?: (event?: React.SyntheticEvent | Event, reason?: SnackbarCloseReason) => void
}

export interface ToastState {
    open: boolean;
    message: string;
    variant: ToastVariants;
}

export const Toast = ({
    open,
    message,
    variant,
    onClose,
    ...rest
}: CustomToastProps) => {
    const theme = useTheme();

    const toastStylesFn = ToastStyles(theme);

    function SlideTransition(props: SlideProps) {
        return <Slide {...props} direction="down" />;
    }

    let icon: IconName;
    switch (variant) {
        case "warning":
            icon = "InformationCircle";
            break;
        case "error":
            icon = "Exclamation";
            break;
        case "success":
            icon = "CheckCircle";
            break;
        default:
            icon = "InformationCircle";
    }

    return (
        <CustomToastComponent 
            TransitionComponent={SlideTransition} 
            autoHideDuration={3000}
            open={open}
            onClose={onClose}
            anchorOrigin={{ vertical: "top", horizontal: "center" }}
            {...rest}
        >
            <Alert
                onClose={onClose}
                severity={variant}
                variant="filled"
                data-cy="custom-toast"
                sx={{
                    ...toastStylesFn[variant],
                }}
                icon={<Icon size="sm" icon={icon} />}
            >
                {message}
            </Alert>
        </CustomToastComponent>
    );
};

export interface ToastProps extends React.ComponentProps<typeof Toast> {
    as?: React.ElementType;
}

Toast.displayName = "Toast";